<script setup lang="ts">
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import CreditCardLogo from '@/components/base/assets/CreditCardLogo.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import ApplePayButton from '@/components/cart/ApplePayButton.vue';
import ExpressCheckout from '@/components/cart/ExpressCheckout.vue';
import { ExpressLocation, useExpressCheckout } from '@/composables/useExpressCheckout';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useProceedToCheckout } from '@/composables/useProceedToCheckout';
import { Money } from '@/utils/money';

const store = useStore();
const { offerApplePay, offerExpressCheckout, offerShopPay } = useExpressCheckout(store);

defineProps<{
  totalCost?: Money;
}>();

const route = useRoute();

const { flags } = useFeatureFlags();
const { isPending, proceedToCheckout } = useProceedToCheckout();

const trackingEventLocation = computed<ExpressLocation>(() =>
  route.path === '/cart/added' ? 'CartAdded' : 'Cart',
);

const showExpressCheckout = computed(() => flags.expressCheckout && route.path === '/cart');

const showExpressCheckoutButtons = ref(false);
</script>

<template>
  <div :class="{ 'pb-4 md:pb-0': flags.cartLayoutUpdates }" data-test="cart-action-block">
    <template v-if="flags.cartLayoutUpdates">
      <ThemedButton
        class="w-full h-12 py-4 md:px-6 lg:px-8 md:text-sm lg:text-base"
        data-test="checkout-button"
        :disabled="isPending"
        @click.prevent="() => proceedToCheckout()"
      >
        <img
          v-if="isPending"
          class="w-4 h-4 mr-2 animate-spin"
          src="@/assets/spinner.svg"
          alt="Loading"
        />
        Proceed to Checkout
        <span v-if="totalCost" class="md:hidden">&nbsp;- {{ money(totalCost) }}</span>
      </ThemedButton>
      <transition name="slide-up">
        <div v-if="offerExpressCheckout" class="relative h-12 mt-2 md:hidden">
          <transition name="fade">
            <ThemedButton
              v-if="!showExpressCheckoutButtons"
              class="absolute top-0 right-0 z-10 w-full h-12 py-4 md:hidden md:px-6 lg:px-8 md:text-sm lg:text-base"
              data-test="checkout-button"
              :disabled="isPending"
              theme="white"
              @click.prevent.stop="() => (showExpressCheckoutButtons = true)"
            >
              Or Check Out Quickly with
              <div class="flex flex-row gap-1 pl-2 text-gray-200">
                <CreditCardLogo v-if="offerApplePay" cardType="apple-pay" class="h-6" />
                <CreditCardLogo v-if="offerExpressCheckout" cardType="paypal" class="h-6" />
                <CreditCardLogo v-if="offerShopPay" cardType="shop-pay" class="h-6" />
              </div>
            </ThemedButton>
          </transition>
          <span class="absolute top-0 left-0 w-full">
            <ExpressCheckout v-if="showExpressCheckout" :trackingEventLocation />
            <ApplePayButton v-else :componentName="trackingEventLocation" />
          </span>
        </div>
      </transition>
      <transition name="fade">
        <div
          v-if="showExpressCheckoutButtons && offerExpressCheckout"
          class="mt-2 text-xs text-center text-gray-800 md:hidden md:text-left md:text-gray-500"
          data-test="disclaimer"
        >
          *Gift options, multiple addresses and other features are unavailable with express checkout
        </div>
      </transition>
    </template>
    <button
      v-else
      class="button primary"
      data-test="checkout-button"
      :disabled="isPending"
      @click.prevent="() => proceedToCheckout()"
    >
      <img
        v-if="isPending"
        class="w-4 h-4 mr-2 animate-spin"
        src="@/assets/spinner.svg"
        alt="spinner"
      />
      Proceed to Checkout
    </button>
    <span :class="{ 'hidden md:block md:visible': flags.cartLayoutUpdates }">
      <ExpressCheckout v-if="showExpressCheckout" :trackingEventLocation />
      <ApplePayButton v-else :componentName="trackingEventLocation" />
    </span>
    <slot />
  </div>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
